import { useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { generateEmailMessageSummary } from '../../../api'
import { EMPTY_OBJECT } from '../../../constants/common'
import { PROFILE_ROUTE_NAMES } from '../../../constants/route'
import {
  getPageBrowserTabTitleWithEntityName,
  useAsyncCallback,
  usePageBrowserTabTitle,
} from '../../../hooks'
import { getAsyncTaskId } from '../../../utils/async'
import { AITextGenerationLocalContextType } from '../../AITextGeneration'
import {
  useEmailProfileActionsContext,
  useEmailProfileActionsMessage,
  useMassMailingAsyncAction,
} from '../Email.hooks'
import { EmailProfile } from '../EmailProfile'

import { useEmailMessageProfileLoaders } from './EmailMessageProfile.hooks'
import {
  getEmailMessageProfileActionsWidgetItems,
  getMessages,
} from './EmailMessageProfile.utils'

function EmailMessageProfile({
  actionsContextProps,
  mailUuid,
  navigation = null,
  profileLoadersRef,
  withWidgets = true,
  onReload,
  onSuccessRead,
  ...restProps
}) {
  const { t } = useTranslation()

  const profileLoaders = useEmailMessageProfileLoaders({
    isReadEnabled: !navigation?.isLoading,
    mailUuid,
    withWidgets,
    onSuccessRead,
  })

  useEffect(() => {
    if (profileLoadersRef) {
      profileLoadersRef.current = profileLoaders
    }
  }, [profileLoaders])

  const {
    entityData,
    handleEntityReload,
    handleReloadProfile,
    handleUpdateEntity,
    isInitiated,
    isLoading,
    loadAISummaryWidgetData,
    loadWidgetsData,
    serverError,
    setSummaryWidgetData,
    summaryData,
    widgetsData,
  } = profileLoaders

  usePageBrowserTabTitle({
    isLoading: !isInitiated,
    pageTitle: getPageBrowserTabTitleWithEntityName({
      name: entityData?.subject,
      title: t('BrowserTab.EmailMessageProfileTabTitleKey'),
    }),
  })

  const messages = useMemo(() => getMessages(entityData), [entityData])

  const { checkAsyncMassMailingTaskStatus } =
    useMassMailingAsyncAction(EMPTY_OBJECT)

  const handleFinishMassMailingReply = useAsyncCallback(() => {
    onReload?.()

    return handleReloadProfile()
  }, [handleReloadProfile, onReload])

  async function handleSuccessReply({ data }) {
    const taskId = getAsyncTaskId(data)

    if (taskId) {
      checkAsyncMassMailingTaskStatus(
        { ...data, task: taskId },
        handleFinishMassMailingReply
      )

      return
    }

    onReload?.()

    return handleReloadProfile()
  }

  return (
    <EmailProfile
      {...restProps}
      actionsContextProps={{
        ...actionsContextProps,
        getPrintPageLink: PROFILE_ROUTE_NAMES.emailMessagePrint,
        useProfileActions: useEmailProfileActionsMessage,
      }}
      entityData={entityData}
      isInitiated={isInitiated}
      isLoading={isLoading}
      loadWidgetsData={loadWidgetsData}
      mailUuid={mailUuid}
      messages={messages}
      messagesListProps={{
        itemProps: {
          actionsConfig: actionsContextProps,
          aiLocalContextType: AITextGenerationLocalContextType.EmailReply,
          replyConfig: { onSuccess: handleSuccessReply },
        },
      }}
      navigation={navigation}
      serverError={serverError}
      summaryProps={{
        data: summaryData,
        generateSummaryParams: { message_id: mailUuid },
        onError: setSummaryWidgetData,
        onGenerateSummary: generateEmailMessageSummary,
        onReloadData: loadAISummaryWidgetData,
        onSuccess: setSummaryWidgetData,
      }}
      widgetsData={widgetsData}
      widgetsProps={{
        actionsProps: {
          getActions: getEmailMessageProfileActionsWidgetItems,
          useActions: useEmailProfileActionsContext,
        },
      }}
      withWidgets={withWidgets}
      onReloadEntity={handleReloadProfile}
      onReloadMessages={handleEntityReload}
      onUpdateEntity={handleUpdateEntity}
      onUpdateMessage={handleUpdateEntity}
    />
  )
}

export default EmailMessageProfile
